import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography,
} from '@mui/material';
import { Facility, FacilityRequest, FacilityType, FACILITY_TYPES } from '../../interfaces/borrowing.interface';
import { BorrowingService } from '../../services/borrowing.service';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import SearchableSelect from '../common/SearchableSelect';
import { useToast } from '../../context/ToastContext';
import { humanizeEnum } from '../../utils/format';
import { helpElement } from '../../help/attrs';
import { useFinancingLookups } from './useFinancingLookups';

/** Chart codes the register is designed around (a suggestion only: any posting account may be picked). */
export const SUGGESTED_CODES: Record<string, string> = {
  principal: '2001030100 term loans / 2002010101 short-term loans (EDF 2001020300)',
  interestExpense: '5004020100 / 5004020200 / 5004010200',
};

interface Props {
  open: boolean;
  institutionId: string;
  facility?: Facility | null;
  onClose: () => void;
  onSaved: (f: Facility) => void;
}

const cell = { flex: '1 1 220px', maxWidth: 320 } as const;

/** Help keys of the facility dialog, by mode (literals so the help key scan sees them). */
const FAC_DLG_HELP: Record<'create' | 'edit', { paper: Record<string, string>; helpKey: string }> = {
  create: { paper: { 'data-help-page': 'fin.facilityCreate.page' }, helpKey: 'fin.facilityCreate.save' },
  edit: { paper: { 'data-help-page': 'fin.facilityEdit.page' }, helpKey: 'fin.facilityEdit.save' },
};

const FacilityFormDialog: React.FC<Props> = ({ open, institutionId, facility, onClose, onSaved }) => {
  const toast = useToast();
  const { accounts, bankAccounts, segments } = useFinancingLookups(institutionId);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState<Record<string, string>>({});
  const isEdit = !!facility;

  useEffect(() => {
    if (!open) return;
    const x = facility;
    setF({
      facilityNo: x?.facilityNo ?? '', name: x?.name ?? '', facilityType: x?.facilityType ?? 'TERM_LOAN',
      lenderName: x?.lenderName ?? '', currencyCode: x?.currencyCode ?? '', sanctionedLimit: x ? String(x.sanctionedLimit) : '',
      validFrom: x?.validFrom ?? '', validTo: x?.validTo ?? '', rateType: x?.rateType ?? 'FIXED',
      spreadPct: x?.spreadPct != null ? String(x.spreadPct) : '', dayCount: x?.dayCount ?? 'ACT_365',
      bankAccountId: x?.bankAccountId ?? '', principalAccountId: x?.principalAccountId ?? '',
      interestExpenseAccountId: x?.interestExpenseAccountId ?? '', interestPayableAccountId: x?.interestPayableAccountId ?? '',
      chargesAccountId: x?.chargesAccountId ?? '', segmentId: x?.segmentId ?? '', remarks: x?.remarks ?? '',
      initialRatePct: '', rateEffectiveFrom: '',
      pcEligibilityPct: x?.pcEligibilityPct != null ? String(x.pcEligibilityPct) : '',
    });
  }, [open, facility]);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => setF(s => ({ ...s, [k]: e.target.value }));
  const setId = (k: string) => (id: string) => setF(s => ({ ...s, [k]: id }));
  const num = (v: string) => (v === '' ? null : Number(v));
  const nz = (v: string) => (v === '' ? null : v);

  const valid = f.facilityNo?.trim() && f.name?.trim() && f.lenderName?.trim() && f.sanctionedLimit !== '' &&
    (f.facilityType === 'OVERDRAFT' || !!f.interestPayableAccountId) &&
    (isEdit || f.initialRatePct !== '');

  const save = async () => {
    setSaving(true);
    try {
      const body: FacilityRequest = {
        institutionId, facilityNo: f.facilityNo.trim(), name: f.name.trim(), facilityType: f.facilityType as FacilityType,
        lenderName: f.lenderName.trim(), bankAccountId: nz(f.bankAccountId), currencyCode: nz(f.currencyCode.toUpperCase()),
        sanctionedLimit: Number(f.sanctionedLimit), validFrom: nz(f.validFrom), validTo: nz(f.validTo),
        rateType: f.rateType as FacilityRequest['rateType'], spreadPct: num(f.spreadPct),
        dayCount: f.dayCount as FacilityRequest['dayCount'], principalAccountId: nz(f.principalAccountId),
        interestExpenseAccountId: nz(f.interestExpenseAccountId), interestPayableAccountId: nz(f.interestPayableAccountId),
        chargesAccountId: nz(f.chargesAccountId), segmentId: nz(f.segmentId), remarks: nz(f.remarks),
        initialRatePct: isEdit ? null : num(f.initialRatePct), rateEffectiveFrom: isEdit ? null : nz(f.rateEffectiveFrom),
        pcEligibilityPct: f.facilityType === 'PACKING_CREDIT' ? num(f.pcEligibilityPct) : null,
      };
      const saved = isEdit ? await BorrowingService.updateFacility(facility!.id, body) : await BorrowingService.createFacility(body);
      toast.showSuccess(isEdit ? 'Facility updated' : 'Facility created');
      onSaved(saved);
    } catch (err) {
      toast.showApiError(err, 'Could not save the facility');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={FAC_DLG_HELP[isEdit ? 'edit' : 'create'].paper as any}>
      <DialogTitle>{isEdit ? 'Edit facility' : 'New borrowing facility'}</DialogTitle>
      <DialogContent>
        <Box data-help-section="fin.facilityDialog.section.terms" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
          <TextField data-help="fin.facility.field.facilityNo" sx={cell} size="small" label="Facility no" required value={f.facilityNo ?? ''} onChange={set('facilityNo')} />
          <TextField data-help="fin.facility.field.name" sx={cell} size="small" label="Name" required value={f.name ?? ''} onChange={set('name')} />
          <TextField data-help="fin.facility.field.type" sx={cell} size="small" select label="Type" value={f.facilityType ?? ''} onChange={set('facilityType')}>
            {FACILITY_TYPES.map(t => <MenuItem key={t} value={t}>{humanizeEnum(t)}</MenuItem>)}
          </TextField>
          {f.facilityType === 'PACKING_CREDIT' && (
            <TextField data-help="fin.facility.field.pcEligibility" sx={cell} size="small" type="number" label="PC eligibility % of LC base" value={f.pcEligibilityPct ?? ''}
              helperText="Blank = no eligibility check. LC base = LC amount less its back-to-back LCs." onChange={set('pcEligibilityPct')} />
          )}
          <TextField data-help="fin.facility.field.lender" sx={cell} size="small" label="Lender" required value={f.lenderName ?? ''} onChange={set('lenderName')} />
          <TextField data-help="fin.facility.field.limit" sx={cell} size="small" type="number" label={`Sanctioned limit${f.currencyCode ? ` (${f.currencyCode.toUpperCase()})` : ''}`} required value={f.sanctionedLimit ?? ''}
            helperText="In the facility currency" onChange={set('sanctionedLimit')} />
          <TextField data-help="fin.facility.field.currency" sx={cell} size="small" label="Currency (blank = BDT)" value={f.currencyCode ?? ''} onChange={set('currencyCode')} />
          <TextField data-help="fin.facility.field.validFrom" sx={cell} size="small" type="date" label="Valid from" InputLabelProps={{ shrink: true }}
            value={f.validFrom ?? ''} onChange={set('validFrom')} />
          <TextField data-help="fin.facility.field.validTo" sx={cell} size="small" type="date" label="Valid to" InputLabelProps={{ shrink: true }}
            value={f.validTo ?? ''} onChange={set('validTo')} />
          <TextField data-help="fin.facility.field.rateType" sx={cell} size="small" select label="Rate type" value={f.rateType ?? 'FIXED'} onChange={set('rateType')}>
            <MenuItem value="FIXED">Fixed</MenuItem>
            <MenuItem value="FLOATING">Floating (base + spread)</MenuItem>
          </TextField>
          <TextField data-help="fin.facility.field.spread" sx={cell} size="small" type="number" label="Spread % (floating)" value={f.spreadPct ?? ''}
            onChange={set('spreadPct')} disabled={f.rateType !== 'FLOATING'} />
          <TextField data-help="fin.facility.field.dayCount" sx={cell} size="small" select label="Day count" value={f.dayCount ?? 'ACT_365'} onChange={set('dayCount')}>
            <MenuItem value="ACT_365">Actual / 365</MenuItem>
            <MenuItem value="ACT_360">Actual / 360</MenuItem>
          </TextField>
          {!isEdit && (
            <>
              <TextField data-help="fin.facility.field.rate" sx={cell} size="small" type="number" label={f.rateType === 'FLOATING' ? 'Base rate %' : 'Annual rate %'}
                required value={f.initialRatePct ?? ''} onChange={set('initialRatePct')} />
              <TextField data-help="fin.facility.field.rateFrom" sx={cell} size="small" type="date" label="Rate effective from" InputLabelProps={{ shrink: true }}
                value={f.rateEffectiveFrom ?? ''} onChange={set('rateEffectiveFrom')} helperText="Blank = valid-from or today" />
            </>
          )}
        </Box>
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Accounts and links</Typography>
        <Box data-help-section="fin.facilityDialog.section.accounts" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={cell}>
            <SearchableSelect data-help="fin.facility.field.bank" options={bankAccounts} value={f.bankAccountId ?? ''} onChange={setId('bankAccountId')}
              getOptionLabel={b => `${b.bankName} ${b.accountNumber}`} label="Bank account (default / OD)" size="small" />
          </Box>
          <Box sx={cell}>
            <SearchableSelect data-help="fin.facility.field.segment" options={segments} value={f.segmentId ?? ''} onChange={setId('segmentId')}
              getOptionLabel={s => `${s.code} - ${s.name}`} label="Segment" size="small" />
          </Box>
          <Box sx={cell}>
            <AccountAutocomplete data-help="fin.facility.field.principalAccount" accounts={accounts} value={f.principalAccountId ?? ''} onChange={setId('principalAccountId')}
              label="Principal (liability) account" size="small" helperText={SUGGESTED_CODES.principal} />
          </Box>
          <Box sx={cell}>
            <AccountAutocomplete data-help="fin.facility.field.interestExpenseAccount" accounts={accounts} value={f.interestExpenseAccountId ?? ''} onChange={setId('interestExpenseAccountId')}
              label="Interest expense account" size="small" helperText={SUGGESTED_CODES.interestExpense} />
          </Box>
          <Box sx={cell}>
            <AccountAutocomplete data-help="fin.facility.field.interestPayableAccount" accounts={accounts} value={f.interestPayableAccountId ?? ''} onChange={setId('interestPayableAccountId')}
              label="Interest payable account" size="small" required={f.facilityType !== 'OVERDRAFT'}
              helperText={f.facilityType === 'OVERDRAFT' ? 'Optional for an overdraft' : 'Required for every facility except an overdraft'} />
          </Box>
          <Box sx={cell}>
            <AccountAutocomplete data-help="fin.facility.field.chargesAccount" accounts={accounts} value={f.chargesAccountId ?? ''} onChange={setId('chargesAccountId')}
              label="Charges / fees account" size="small" />
          </Box>
          <TextField data-help="fin.facility.field.remarks" size="small" label="Remarks" multiline minRows={2} sx={{ flex: '1 1 100%' }} value={f.remarks ?? ''} onChange={set('remarks')} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button data-help="fin.facility.dlgCancel" onClick={onClose}>Cancel</Button>
        <Button {...helpElement(FAC_DLG_HELP[isEdit ? 'edit' : 'create'].helpKey)} variant="contained" onClick={save} disabled={!valid || saving}>{isEdit ? 'Save' : 'Create'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FacilityFormDialog;
